import { AbilityBuilder } from '@casl/ability'
import { createContextualCan } from '@casl/react'
import React, { createContext, useContext, useMemo } from 'react'

import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useCurrentMerchantUser from 'hooks/useCurrentMerchantUser'
import useFlag from 'hooks/useFlag'
import { Fields as Merchant } from 'models/Merchant'
import type { Fields as MerchantUser, Role } from 'models/MerchantUser'
import { defineAbilitiesFromPermissions } from './permissionMap'

export const AbilityContext = createContext<any>(null)

export const Can = createContextualCan(AbilityContext.Consumer)

export const AbilityProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const merchant = useCurrentMerchant()
  const merchantUser = useCurrentMerchantUser()
  const useDynamicPerms = useFlag('role-based-access-control', false)

  const ability = useMemo(() => {
    const permissions =
      merchantUser?.permissionsForMerchantId(merchant?.id) || {}
    const hasRbacPermissions = Object.keys(permissions).length > 0

    return useDynamicPerms && hasRbacPermissions
      ? defineAbilitiesFromPermissions(permissions, merchant)
      : defineAbilitiesFor(merchantUser, merchant)
    // merchantUser and merchant are stable refs only because their hooks
    // use equality functions (shallowEqual / jsonEqual). Using raw
    // useSelector(selectCurrentMerchantUser) or similar here will
    // reintroduce the infinite render loop — always use the hooks.
  }, [merchantUser, merchant, useDynamicPerms])

  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  )
}

export function useAbility() {
  const ability = useContext(AbilityContext)

  if (ability === undefined) {
    throw new Error('Needs to be in an AbilityProvider')
  }
  return ability
}

// permissions default to off, so must be explicitly granted
export function defineAbilitiesFor(
  merchantUser: MerchantUser | null,
  merchant: Merchant | null
) {
  if (!merchantUser || !merchant) {
    return AbilityBuilder.define(can => {
      can('manage', 'Login')
    })
  }
  //@ts-ignore
  return defineAbilitiesForRoles(
    merchantUser.rolesForMerchantId(merchant.id),
    merchant
  )
}

export function defineAbilitiesForRoles(roles: Role[], merchant: Merchant) {
  return AbilityBuilder.define((can, cannot) => {
    if (roles.length === 0 && merchant.merchant_type !== 'Mall') {
      can('manage', 'Login')
      return
    }

    // Apply all `can` rules first

    if (roles.includes('manager')) {
      cannot('manage', 'all')
      can('targetCategorizedLocations', 'Campaign')
      can('manage', 'AllCampaigns')
      can('manage', 'TargetedCampaign')
      can('manage', 'CampaignReports')
      can('access', 'PurchasesReport')
      cannot('manage', 'OperationalReports')
      can('manage', 'Feedback')
      cannot('manage', 'Points')
      can('manage', 'Users')
      can('manage', 'UsersByIdentifier')
      can('manage', 'Segments')
      cannot('manage', 'LegacyReports')
      can('manage', 'OrderingManagement')
      can('access', 'NpsReports')
      // Mirrors the RBAC `reporting_location` bundle, which grants
      // LocationReports and InsightsLab together — this role already holds
      // the legacy equivalent (Feedback/NpsReports).
      can('access', 'InsightsLab')
      can('manage', 'SmartLinks')
      can('manage', 'Games')
    }

    // Removes previous role access if these roles are present
    if (roles.includes('accountant')) {
      cannot('manage', 'all')
      can('access', 'Billing')
      can('access', 'Points')
      can('access', 'RedeemManager')
    }

    // Removes previous role access if these roles are present
    if (roles.includes('customer_service_read')) {
      cannot('manage', 'all')
      can('access', 'Feedback')
      can('access', 'NpsReports')
      can('access', 'PurchasesReport')
      can('access', 'Users')
      can('access', 'UsersByIdentifier')
    }

    // Removes previous role access if these roles are present
    if (roles.includes('customer_service_write')) {
      cannot('manage', 'all')
      can('manage', 'Feedback')
      can('access', 'NpsReports')
      can('access', 'PurchasesReport')
      can('access', 'Users')
      can('manage', 'UsersByIdentifier')
    }

    if (roles.includes('admin')) {
      can('manage', 'all')
      can('manage', 'Feedback')
      can('access', 'Broadcasts')
      can('access', 'TargetedCampaign::Location')
      can('access', 'UserSupport')
      can('access', 'AnsaReports')
      can('access', 'InsightsLab')
      can('targetCategorizedLocations', 'Campaign')
      can('review', 'Campaign')
      can('schedule', 'Campaign')
      can('targetAllLocations', 'Campaign')
      can('manage', 'RedeemManager')
      can('manage', 'Points')
      can('manage', 'CMS')
      can('manage', 'Billing')
      can('manage', 'MerchantUsers')
      can('manage', 'Users')
      can('manage', 'UsersByIdentifier')
      can('manage', 'Segments')
      can('manage', 'LegacyReports')
      can('manage', 'OperationalReports')
      can('manage', 'LocationReports')
      can('manage', 'AverageCheckReport')
      can('manage', 'PurchasesReport')
      can('manage', 'NpsReports')
      can('manage', 'OrderingManagement')
      cannot('manage', 'Login')
    }

    // Apply all `cannot` rules
    if (merchant.merchant_type === 'Mall') {
      cannot('targetCategorizedLocations', 'Campaign')
      cannot('access', 'TargetedCampaign::Location')
      cannot('targetAllLocations', 'Campaign')
      cannot('access', 'Feedback')
      cannot('access', 'OperationalReports')
      cannot('access', 'AverageCheckReport')
      cannot('access', 'PurchasesReport')
      cannot('access', 'InsightsLab')
    }
  })
}
